import {ExtendedTableFilter} from "@feature/commons/extended-table-filter";


export const getColumns = (intl, fetchColumnValues, isColumnSearchLoading) => {
  
  // extended filters utilities
  const fetch = (search, limit, field) => {
    const params = ["/products/search", field, search];
    if (limit)
      params.push(limit);
    return fetchColumnValues(...params)
      .then((res) => res.map((value) => ({label: value, value})))
  }
  const getExtendedFilter = (
    { columnDef, onFilterChanged },
    hasNullFlag=true,
    hasSearch=true,
    isAsync=true,
  ) => (
    <ExtendedTableFilter
      fetch={(search, limit) => fetch(search, limit, columnDef.field)}
      loading={isColumnSearchLoading(columnDef.field)}
      columnDef={columnDef}
      onFilterChanged={onFilterChanged}
      hasNullFlag={hasNullFlag}
      hasSearch={hasSearch}
      limit={50}
      isAsync={isAsync}
    />
  );
  const getPaginationSpecs = (name) => ({
    pagination: {
      spech: {
        column_name: name,
        filter_operator: "in",
        value_parser: (value) => value,
        null_filter: true,
      }
    }
  });

  // rendering utilities
  const renderLink = (url) => {
    if (!url || !url.startsWith("http")) return "";
    const lst = url.split(",");
    return (
      <ul className={`${lst.length > 1 ? "list-disc" : "list-none"} list-inside`}>
        {lst.map((url, idx) => {
          const shortened = url.length > 40 ? url.substring(0, 40) + "..." : url;
          return (
            <li key={idx} className="whitespace-nowrap">
              <a href={url} target="_blank" className="text-kerakoll-700 whitespace-nowrap">
                {shortened}
              </a>
            </li>
          )
        })}
      </ul>
    )
  }

  return [
    {
      title: intl.formatMessage({id: "active"}),
      field: "active",
      type: "boolean",
      defaultFilter: "checked", // 'checked', 'unchecked', or undefined
    },
    {
      title: intl.formatMessage({ id: "code" }),
      field: "art_cod",
      defaultSort: "asc",
      type: "string",
      filterComponent: getExtendedFilter,
      ...getPaginationSpecs("art_cod"),
    },
    {
      title: intl.formatMessage({ id: "description" }),
      field: "art_des_cmpy",
      type: "string",
      filterComponent: getExtendedFilter,
      ...getPaginationSpecs("art_des_cmpy"),
    },
    {
      title: intl.formatMessage({ id: "price" }),
      field: "prezzo",
      type: "numeric",
      render: ({prezzo}) => !!prezzo || prezzo === 0
        ? Intl.NumberFormat("it-IT", {style: "currency", currency: "EUR"}).format(prezzo)
        : "",
      filterComponent: (props) => getExtendedFilter(props, true, false),
      ...getPaginationSpecs("prezzo"),
    },
    {
      title: intl.formatMessage({ id: "production_line_code" }),
      field: "prod_line_cod",
      type: "string",
      filterComponent: (props) => getExtendedFilter(props, true, true, false),
      ...getPaginationSpecs("prod_line_cod"),
    },
    {
      title: intl.formatMessage({ id: "ref_company_code" }),
      field: "ref_cmpy_cod",
      type: "string",
      filterComponent: (props) => getExtendedFilter(props, true, true, false),
      ...getPaginationSpecs("ref_cmpy_cod"),
    },
    {
      title: intl.formatMessage({ id: "mu" }),
      field: "base_um",
      type: "string",
      filterComponent: getExtendedFilter,
      ...getPaginationSpecs("base_um"),
    },
    {
      title: intl.formatMessage({ id: "minimum_quantity" }),
      field: "min_qty",
      type: "numeric",
      filterComponent: (props) => getExtendedFilter(props, true, false),
      ...getPaginationSpecs("min_qty"),
    },
    {
      title: intl.formatMessage({ id: "budget_class_code" }),
      field: "bdg_cls_cod",
      type: "string",
      filterComponent: (props) => getExtendedFilter(props, true, true, false),
      ...getPaginationSpecs("bdg_cls_cod"),
    },
    {
      title: intl.formatMessage({ id: "statistical_code" }),
      field: "prod_stat_cod",
      type: "string",
      filterComponent: getExtendedFilter,
      ...getPaginationSpecs("prod_stat_cod"),
    },
    {
      title: intl.formatMessage({ id: "statistical_code_description" }),
      field: "prod_stat_des",
      type: "string",
      filterComponent: getExtendedFilter,
      ...getPaginationSpecs("prod_stat_des"),
    },
    {
      title: intl.formatMessage({ id: "pso_art_status_code" }),
      field: "pso_art_status_cod",
      type: "string",
      filterComponent: getExtendedFilter,
      ...getPaginationSpecs("pso_art_status_cod"),
    },
    {
      title: intl.formatMessage({ id: "yield" }),
      field: "resa",
      type: "numeric",
      filterComponent: (props) => getExtendedFilter(props, true, false),
      ...getPaginationSpecs("resa"),
    },
    {
      title: intl.formatMessage({ id: "yield_mu" }),
      field: "unita_mis_resa",
      type: "string",
      filterComponent: getExtendedFilter,
      ...getPaginationSpecs("unita_mis_resa"),
    },
    {
      title: intl.formatMessage({ id: "min_thickness" }),
      field: "spessore_min",
      type: "numeric",
      filterComponent: (props) => getExtendedFilter(props, true, false),
      ...getPaginationSpecs("spessore_min"),
    },
    {
      title: intl.formatMessage({ id: "technical_sheet_link" }),
      field: "link_scheda_tecnica",
      type: "string",
      render: ({link_scheda_tecnica: url}) => renderLink(url),
      filterComponent: getExtendedFilter,
      ...getPaginationSpecs("link_scheda_tecnica"),
    },
    ...["link_dop", "link_cam", "link_epd", "link_sds", "link_voc"].map((c) => {
      const defaultMessage = c.split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
      return ({
        title: intl.formatMessage({id: c, defaultMessage}),
        field: c,
        type: "string",
        render: ({[c]: url}) => renderLink(url),
        filterComponent: getExtendedFilter,
        ...getPaginationSpecs(c),
      })
    }),
    {
      title: intl.formatMessage({ id: "video_link" }),
      field: "link_video",
      type: "string",
      render: ({link_video: url}) => renderLink(url),
      filterComponent: getExtendedFilter,
      ...getPaginationSpecs("link_video"),
    },
    {
      title: intl.formatMessage({ id: "image_link" }),
      field: "link_image",
      type: "string",
      render: ({link_image: url}) => renderLink(url),
      filterComponent: getExtendedFilter,
      ...getPaginationSpecs("link_image"),
    },
    {
      title: intl.formatMessage({ id: "business" }),
      field: "business_id",
      lookup: {1: "Kerakoll", 2: "Knauf"},
      pagination: {
        spech: {
          column_name: "business_id",
          filter_operator: "in",
          value_parser: (value) => value.length > 0 ? value.map((x) => parseInt(x)) : null,
        }
      }
    },
    {
      title: intl.formatMessage({ id: "has_kpi" }),
      field: "flg_kpi",
      type: "boolean",
    },
    {
      title: intl.formatMessage({ id: "custom" }),
      field: "manual",
      editable: "never",
      type: "boolean",
    },
  ];
}

export const OPTIONS = {
  filtering: true,
  pageSize: 10,
  searchFieldAlignment: "left",
  searchFieldStyle: {
    marginTop: "2px",
  },
  showTitle: false,
  toolbar: false,
  filterCellStyle: { maxWidth: 250 },
  headerStyle: { whiteSpace: "nowrap", fontWeight: "bold" },
  cellStyle: { fontSize: 12, padding: 0 },
};

export const getLocalization = (intl) => ({
  header: {
    actions: "",
  },
  body: {
    editRow: {
      deleteText: intl.formatMessage({ id: "delete_user" }),
    },
  },
  pagination: {
    labelDisplayedRows: "{from}-{to} of {count} products",
    labelRowsSelect: "Products",
  },
});
